(function () {
	window.__accordionScroll = window.__accordionScroll || {
		started: false,
		swiper: null,
		mobileController: null,
		desktopController: null,
		gsapReady: false,
		refreshTimer: null,
		refreshHandlerBound: false,
		_mm: null
	};

	const ACCORDION_SCROLL_DESKTOP_STEP = 1000;
	const ACCORDION_SCROLL_MOBILE_STEP = 756;
	const ACCORDION_LAST_STEP_HOLD_RATIO = 0.8;
	const ACCORDION_MOBILE_HEADER_OFFSET_FALLBACK = 64;

	function getAccordionSection() {
		return document.querySelector('[data-compact-interactive-journey]');
	}

	function scheduleAccordionRefresh(preserveState) {
		if (!window.ScrollTrigger) return;

		const sharedState = window.__accordionScroll;
		if (sharedState.refreshTimer) {
			window.clearTimeout(sharedState.refreshTimer);
		}

		sharedState.refreshTimer = window.setTimeout(function () {
			sharedState.refreshTimer = null;
			// Resize/orientation events can fire in bursts while the browser is still reflowing.
			// Debouncing here avoids repeated refreshes and gives ScrollTrigger one stable layout pass.
			window.ScrollTrigger.refresh();

			if (preserveState !== false) {
				window.requestAnimationFrame(function () {
					sharedState.desktopController?.syncToTrigger?.({ force: true, instantLayout: true });
					sharedState.mobileController?.syncToTrigger?.({ force: true });
				});
			}
		}, 180);
	}

	function bindAccordionRefreshHandlers() {
		if (window.__accordionScroll.refreshHandlerBound) return;
		window.__accordionScroll.refreshHandlerBound = true;

		window.addEventListener('resize', function () { scheduleAccordionRefresh(true); });
		window.addEventListener('orientationchange', function () { scheduleAccordionRefresh(true); });
	}

	window.getAccordionMobileHeaderOffset = window.getAccordionMobileHeaderOffset || function getAccordionMobileHeaderOffset() {
		const selectors = [
			'.main-header',
			'.site-header',
			'.header-wrapper',
			'.sticky-header',
			'#shopify-section-header'
		];

		for (const selector of selectors) {
			const header = document.querySelector(selector);
			if (!header) continue;
			const rect = header.getBoundingClientRect();
			if (rect.height > 0) return rect.height;
		}

		return ACCORDION_MOBILE_HEADER_OFFSET_FALLBACK;
	};

	window.setAccordionProgressBars = window.setAccordionProgressBars || function setAccordionProgressBars(sectionEl, progress, count) {
		if (!sectionEl) return;

		const state = sectionEl.__accordionProgressState || (sectionEl.__accordionProgressState = {
			frame: null,
			progress: 0,
			count: 0
		});

		state.progress = progress;
		state.count = count;

		if (state.frame) return;

		state.frame = window.requestAnimationFrame(function () {
			state.frame = null;

			const selectors = [
				'.left-accordion.dk_show .single-accordion .progress-bar',
				'.left-accordion.mb_show .single-accordion .progress-bar',
				'.left-accordion-overlay.mb_show .extera-con .progress-bar'
			];

			selectors.forEach(selector => {
				const bars = sectionEl.querySelectorAll(selector);
				if (!bars.length) return;

				if (state.count <= 1) {
					bars.forEach((bar, index) => {
						const fillValue = index === 0 ? '1' : '0';
						if (bar.style.getPropertyValue('--progress-fill') !== fillValue) {
							bar.style.setProperty('--progress-fill', fillValue);
						}
					});
					return;
				}

				const stepProgress = Math.max(0, Math.min(state.progress, 1)) * (state.count - 1);
				bars.forEach((bar, index) => {
					const fill = Math.max(0, Math.min(stepProgress - index, 1));
					const fillValue = String(Math.round(fill * 1000) / 1000);
					if (bar.style.getPropertyValue('--progress-fill') !== fillValue) {
						bar.style.setProperty('--progress-fill', fillValue);
					}
				});
			});
		});
	};

	// ─── Shared controller base ───────────────────────────────────────────────────
	// Both the desktop accordion and the mobile swiper share the same progress-mapping
	// math, trigger/pin lifecycle, and scroll-tween management. This factory creates
	// that common foundation so logic lives in one place instead of two.
	function createBaseController() {
		return {
			trigger: null,
			pinned: false,
			progress: 0,
			activeIndex: -1,
			scrollTween: null,

			clamp(value, min, max) {
				return Math.max(min, Math.min(value, max));
			},

			// Must be overridden by each controller.
			getCount() { return 0; },

			getMaxIndex() {
				return Math.max(this.getCount() - 1, 0);
			},

			getTotalScrollUnits() {
				return this.getMaxIndex() + ACCORDION_LAST_STEP_HOLD_RATIO;
			},

			progressFromIndex(index) {
				const maxIndex = this.getMaxIndex();
				if (!maxIndex) return 0;
				return this.clamp(index, 0, maxIndex) / maxIndex;
			},

			// ScrollTrigger progress → content playhead (excludes trailing hold region).
			playProgressFromScrollProgress(scrollProgress) {
				const activeUnits = this.getMaxIndex();
				if (!activeUnits) return 0;
				return this.clamp(
					(this.clamp(scrollProgress, 0, 1) * this.getTotalScrollUnits()) / activeUnits,
					0, 1
				);
			},

			// Content playhead → ScrollTrigger progress (inverse of above).
			scrollProgressFromPlayProgress(playProgress) {
				const activeUnits = this.getMaxIndex();
				const totalUnits = this.getTotalScrollUnits();
				if (!activeUnits || !totalUnits) return 0;
				return this.clamp(
					(this.clamp(playProgress, 0, 1) * activeUnits) / totalUnits,
					0, 1
				);
			},

			cancelScrollTween() {
				if (this.scrollTween) {
					this.scrollTween.kill();
					this.scrollTween = null;
				}
			},

			setTrigger(trigger) { this.trigger = trigger; },

			setPinned(isPinned) {
				this.pinned = !!isPinned;
				if (!isPinned) this.cancelScrollTween();
			}
		};
	}

	// ─── initAccordionScrollPlay ──────────────────────────────────────────────────
	window.initAccordionScrollPlay = window.initAccordionScrollPlay || function initAccordionScrollPlay(opts) {
		const section = opts?.section || getAccordionSection();
		if (!section) return;

		function getMobileCount() {
			return window.swiperInstance?.slides?.length || 0;
		}

		const desktopController = window.__accordionScroll.desktopController || Object.assign(createBaseController(), {
			getCount() {
				return section.querySelectorAll('.left-accordion.dk_show .single-accordion').length;
			},

			getScrollProgress() {
				if (!this.trigger) return 0;
				const distance = this.trigger.end - this.trigger.start;
				if (!distance) return 0;
				const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
				return this.clamp((scrollTop - this.trigger.start) / distance, 0, 1);
			},

			applyIndex(index, opts) {
				const normalizedIndex = this.clamp(index, 0, this.getMaxIndex());
				const options = opts || {};
				// Preserve smooth scroll progress for the progress bar while still snapping UI to an item.
				const progress = typeof options.progressOverride === 'number'
					? this.clamp(options.progressOverride, 0, 1)
					: this.progressFromIndex(normalizedIndex);

				this.activeIndex = normalizedIndex;
				this.progress = progress;

				if (!options.force && typeof window.getOpenAccordionIndex === 'function' && window.getOpenAccordionIndex() === normalizedIndex) {
					window.setAccordionProgressBars(section, progress, this.getCount());
					return;
				}

				if (typeof window.openAccordion === 'function') {
					window.openAccordion(normalizedIndex, { force: options.force, instantLayout: options.instantLayout });
				} else {
					section.querySelectorAll('.left-accordion.dk_show .single-accordion')[normalizedIndex]?.click();
				}

				window.setAccordionProgressBars(section, progress, this.getCount());
			},

			setIndex(index, opts) {
				const normalizedIndex = this.clamp(index, 0, this.getMaxIndex());
				const options = opts || {};
				this.applyIndex(normalizedIndex, options);
				if (options.syncScroll && this.pinned) {
					this.syncScrollToProgress(this.progress, options.scrollDuration || 0);
				}
			},

			handleScrollUpdate(self) {
				// ScrollTrigger drives a continuous progress value; the accordion UI follows the nearest step.
				const playProgress = this.playProgressFromScrollProgress(self.progress);
				const activeUnits = this.getMaxIndex();
				const nextIndex = activeUnits ? Math.floor(playProgress * activeUnits) : 0;
				this.applyIndex(nextIndex, { progressOverride: playProgress });
			},

			syncScrollToProgress(progress, duration) {
				if (!this.trigger) return;

				const targetScrollProgress = this.scrollProgressFromPlayProgress(progress);
				const targetScroll = this.trigger.start + ((this.trigger.end - this.trigger.start) * targetScrollProgress);
				const scrollState = { value: window.pageYOffset || document.documentElement.scrollTop || 0 };

				this.cancelScrollTween();

				if (!duration) {
					window.scrollTo(0, targetScroll);
					return;
				}

				this.scrollTween = gsap.to(scrollState, {
					value: targetScroll,
					duration,
					ease: 'power2.out',
					overwrite: true,
					onUpdate: () => { window.scrollTo(0, scrollState.value); },
					onComplete: () => { this.scrollTween = null; },
					onInterrupt: () => { this.scrollTween = null; }
				});
			},

			syncToTrigger(opts) {
				const options = opts || {};

				if (this.pinned && this.trigger) {
					// During/after refresh, rebuild the active desktop state from the current scroll position.
					const playProgress = this.playProgressFromScrollProgress(this.getScrollProgress());
					const activeUnits = this.getMaxIndex();
					const currentIndex = activeUnits ? Math.floor(playProgress * activeUnits) : 0;
					this.applyIndex(currentIndex, Object.assign({}, options, { progressOverride: playProgress }));
					return;
				}

				const fallbackIndex = typeof window.getOpenAccordionIndex === 'function'
					? window.getOpenAccordionIndex()
					: Math.max(this.activeIndex, 0);
				this.applyIndex(fallbackIndex < 0 ? 0 : fallbackIndex, options);
			}
		});

		window.__accordionScroll.desktopController = desktopController;

		ScrollTrigger.getAll().forEach(st => { if (st.trigger === section) st.kill(); });

		// Revert any previous matchMedia context before creating a fresh one.
		if (window.__accordionScroll._mm) {
			window.__accordionScroll._mm.revert();
		}

		const mm = gsap.matchMedia();
		window.__accordionScroll._mm = mm;

		mm.add('(min-width: 900px)', function () {
			const count = desktopController.getCount();
			if (count <= 1) return;

			const totalUnits = (count - 1) + ACCORDION_LAST_STEP_HOLD_RATIO;

			function onEnterOrBack(self) {
				desktopController.setTrigger(self);
				desktopController.setPinned(true);
				desktopController.syncToTrigger({ force: true, instantLayout: true });
			}

			const st = ScrollTrigger.create({
				trigger: section,
				start: 'top top',
				end: () => `+=${totalUnits * ACCORDION_SCROLL_DESKTOP_STEP}`,
				pin: true,
				invalidateOnRefresh: true,
				scrub: true,
				anticipatePin: 1,
				onRefreshInit() { desktopController.syncToTrigger({ force: true, instantLayout: true }); },
				onRefresh(self) {
					desktopController.setTrigger(self);
					desktopController.syncToTrigger({ force: true, instantLayout: true });
				},
				onEnter: onEnterOrBack,
				onLeave() { desktopController.setPinned(false); },
				onEnterBack: onEnterOrBack,
				onLeaveBack() { desktopController.setPinned(false); },
				onUpdate(self) { desktopController.handleScrollUpdate(self); }
			});

			desktopController.setTrigger(st);
			desktopController.syncToTrigger({ force: true, instantLayout: true });

			return function () {
				desktopController.setPinned(false);
				desktopController.setTrigger(null);
			};
		});

		mm.add('(max-width: 899px)', function () {
			const controller = window.__accordionScroll?.mobileController;
			const count = controller?.getCount?.() || getMobileCount();
			if (!controller || count <= 1) return;

			const totalUnits = (count - 1) + ACCORDION_LAST_STEP_HOLD_RATIO;

			function onEnterOrBack(self) {
				controller.setTrigger(self);
				controller.setPinned(true);
				controller.setProgress(controller.playProgressFromScrollProgress(self.progress), { force: true });
			}

			const st = ScrollTrigger.create({
				trigger: section,
				start: () => `top top+=${window.getAccordionMobileHeaderOffset()}`,
				end: () => `+=${totalUnits * ACCORDION_SCROLL_MOBILE_STEP}`,
				pin: true,
				invalidateOnRefresh: true,
				scrub: true,
				anticipatePin: 1,
				onRefresh(self) {
					controller.refresh();
					controller.setProgress(controller.playProgressFromScrollProgress(self.progress), { force: true });
				},
				onEnter: onEnterOrBack,
				onLeave() {
					controller.setPinned(false);
					controller.setIndex(controller.getNearestIndex(), { instant: true, force: true });
				},
				onEnterBack: onEnterOrBack,
				onLeaveBack() {
					controller.setPinned(false);
					controller.setIndex(controller.getNearestIndex(), { instant: true, force: true });
				},
				onUpdate(self) { controller.handleScrollUpdate(self); }
			});

			controller.setTrigger(st);
			controller.refresh();

			return function () {
				controller.setPinned(false);
				controller.setTrigger(null);
			};
		});
	};

	window.initAccordionScrollListener = function initAccordionScrollListener(swiperInstance, sectionEl) {
		if (swiperInstance) {
			window.__accordionScroll.swiper = swiperInstance;
		}

		const sharedState = window.__accordionScroll;
		const section = sectionEl || getAccordionSection();

		function play() {
			if (typeof window.initAccordionScrollPlay === 'function') {
				window.initAccordionScrollPlay({ swiper: sharedState.swiper || null, section });
			}
		}

		// Already fully initialized — re-run play setup directly (e.g. after a section reload).
		if (sharedState.gsapReady) {
			play();
			return;
		}

		// Guard against registering the load listener more than once.
		if (sharedState.started) return;
		sharedState.started = true;

		function init() {
			gsap.registerPlugin(ScrollTrigger);
			sharedState.gsapReady = true;
			bindAccordionRefreshHandlers();
			play();
		}

		// Fast path: GSAP is already on the page (normal script load order).
		if (window.gsap && window.ScrollTrigger) {
			init();
			return;
		}

		// Fallback: GSAP is loading async/defer. window.load is the last reasonable
		// point to wait; if it has already fired without GSAP, there is nothing to do.
		if (document.readyState === 'complete') return;

		window.addEventListener('load', function () {
			if (window.gsap && window.ScrollTrigger) init();
		}, { once: true });
	};

	document.addEventListener('DOMContentLoaded', function () {
		const section = getAccordionSection();
		if (!section) return;

		// ─── Desktop accordion ────────────────────────────────────────────────────
		const accordions = section.querySelectorAll('.left-accordion.dk_show .single-accordion');
		const images = section.querySelectorAll('.premium-accordion-image .image-wrapp');
		const fullscreenImages = section.querySelectorAll('.fullscreen-bg-image');
		const arrow = section.querySelector('.select-arrow');
		const accordionWrapper = section.querySelector('.left-accordion.dk_show');

		if (accordions.length && arrow && accordionWrapper) {
			function moveArrow(activeIndex) {
				const activeItem = accordions[activeIndex];
				const wrapperRect = accordionWrapper.getBoundingClientRect();
				const itemRect = activeItem.getBoundingClientRect();
				const offset = (itemRect.top - wrapperRect.top) + (activeItem.offsetHeight / 2) - (arrow.offsetHeight / 2);
				arrow.style.transform = `translateY(${offset}px)`;
			}

			function updateOpacity(activeIndex) {
				accordions.forEach((item, index) => {
					item.classList.remove('level-1', 'level-2', 'level-3');
					const distance = Math.abs(index - activeIndex);
					if (distance === 1) item.classList.add('level-1');
					else if (distance === 2) item.classList.add('level-2');
					else if (distance >= 3) item.classList.add('level-3');
				});
			}

			// Stores each .desc-wrap's original transition so it can be suppressed during
			// GSAP refresh-driven layout passes. Active CSS transitions during measurement
			// corrupt the pin-spacer height and push the section far down the page.
			const _descTransitions = new WeakMap();

			function setDesktopTransitionLock(isLocked) {
				accordions.forEach(acc => {
					const desc = acc.querySelector('.desc-wrap');
					if (!desc) return;

					if (isLocked) {
						if (!_descTransitions.has(desc)) {
							_descTransitions.set(desc, desc.style.transition || '');
						}
						desc.style.transition = 'none';
					} else {
						const original = _descTransitions.get(desc);
						if (typeof original !== 'undefined') {
							desc.style.transition = original;
							_descTransitions.delete(desc);
						} else {
							desc.style.removeProperty('transition');
						}
					}
				});
			}

			let activeDesktopAccordionIndex = -1;

			function openAccordion(index, opts) {
				const options = opts || {};
				const current = accordions[index];
				if (!current) return;

				if (options.instantLayout) setDesktopTransitionLock(true);

				if (!options.force && activeDesktopAccordionIndex === index) {
					moveArrow(index);
					if (options.instantLayout) {
						window.requestAnimationFrame(function () { setDesktopTransitionLock(false); });
					}
					return;
				}

				activeDesktopAccordionIndex = index;
				section.classList.remove('popup-active');

				accordions.forEach((acc, i) => {
					acc.classList.remove('active');
					const desc = acc.querySelector('.desc-wrap');
					if (desc) desc.style.maxHeight = null;
					if (images[i]) images[i].classList.remove('active');
				});

				fullscreenImages.forEach(img => img.classList.remove('active'));

				const isFullBg = current.getAttribute('data-full-bg') === 'true';
				const blockId = current.getAttribute('data-block-id');
				const currentDesc = current.querySelector('.desc-wrap');

				current.classList.add('active');
				section.classList.toggle('fullscreen-mode', isFullBg);

				if (currentDesc) currentDesc.style.maxHeight = `${currentDesc.scrollHeight}px`;

				if (isFullBg) {
					section.querySelector(`.fullscreen-bg-image.${blockId}`)?.classList.add('active');
				} else if (images[index]) {
					images[index].classList.add('active');
				}

				updateOpacity(index);

				if (currentDesc) {
					currentDesc.addEventListener('transitionend', function handler() {
						moveArrow(index);
						currentDesc.removeEventListener('transitionend', handler);
					});
				} else {
					moveArrow(index);
				}

				if (options.instantLayout) {
					moveArrow(index);
					window.requestAnimationFrame(function () { setDesktopTransitionLock(false); });
				}
			}

			accordions.forEach((item, index) => {
				item.addEventListener('click', function () {
					const desktopController = window.__accordionScroll?.desktopController;
					const shouldSyncScroll = !!desktopController?.pinned;

					if (!item.classList.contains('active')) openAccordion(index);

					if (desktopController) {
						desktopController.setIndex(index, {
							force: true,
							syncScroll: shouldSyncScroll,
							scrollDuration: shouldSyncScroll ? 0.3 : 0
						});
					}
				});
			});

			openAccordion(0, { force: true, instantLayout: true });
			window.openAccordion = openAccordion;
			window.getOpenAccordionIndex = function () { return activeDesktopAccordionIndex; };
		}

		// ─── Mobile swiper ────────────────────────────────────────────────────────
		const mediaQuery = window.matchMedia('(max-width: 899px)');
		let swiperInstance = null;
		let hasInteracted = false;

		const mobileController = Object.assign(createBaseController(), {
			swiper: null,
			touchStartX: 0,
			touchStartTime: 0,
			isApplyingProgress: false,
			isSyncingScroll: false,

			getCount() {
				return this.swiper?.slides?.length
					|| section.querySelectorAll('.left-accordion.mb_show .swiper-slide').length
					|| 0;
			},

			getNearestIndex(progress) {
				const maxIndex = this.getMaxIndex();
				const p = typeof progress === 'number' ? progress : this.progress;
				if (!maxIndex) return 0;
				return Math.round(this.clamp(p, 0, 1) * maxIndex);
			},

			getScrollIndex(progress) {
				const maxIndex = this.getMaxIndex();
				const p = typeof progress === 'number' ? progress : this.progress;
				if (!maxIndex) return 0;
				return Math.floor(this.clamp(p, 0, 1) * maxIndex);
			},

			progressFromTranslate(translate) {
				if (!this.swiper) return this.progress;
				const start = this.swiper.minTranslate();
				const end = this.swiper.maxTranslate();
				const distance = end - start;
				// Normalise Swiper's pixel translate into a 0..1 playhead so drag, scroll,
				// and button clicks all share one state model.
				if (!distance) return 0;
				return this.clamp((translate - start) / distance, 0, 1);
			},

			translateFromProgress(progress) {
				if (!this.swiper) return 0;
				const start = this.swiper.minTranslate();
				const end = this.swiper.maxTranslate();
				return start + ((end - start) * this.clamp(progress, 0, 1));
			},

			refresh() {
				if (!this.swiper) return;
				this.swiper.update();
				this.swiper.updateSlides();
				this.swiper.updateProgress(this.swiper.translate);
				window.setAccordionProgressBars(section, this.progress, this.getCount());
			},

			updateActive(index, opts) {
				const options = opts || {};
				const normalizedIndex = this.clamp(index, 0, this.getMaxIndex());

				if (!options.force && normalizedIndex === this.activeIndex) {
					if (options.openPopup) section.classList.add('popup-active');
					else if (options.closePopup) section.classList.remove('popup-active');
					return;
				}

				this.activeIndex = normalizedIndex;
				hasInteracted = true;

				const slides = Array.from(section.querySelectorAll('.left-accordion.mb_show .swiper-slide'));
				const imgs = Array.from(section.querySelectorAll('.image-wrapp'));
				const content = Array.from(section.querySelectorAll('.left-accordion-overlay.mb_show .extera-con'));
				const fullscreenBgs = section.querySelectorAll('.fullscreen-bg-image');
				const activeSlide = slides[normalizedIndex];
				const isFullBg = activeSlide?.getAttribute('data-full-bg') === 'true';
				const blockId = activeSlide?.getAttribute('data-block-id');

				slides.forEach(s => s.classList.remove('active'));
				imgs.forEach(i => i.classList.remove('active'));
				content.forEach(c => c.classList.remove('active'));
				fullscreenBgs.forEach(bg => bg.classList.remove('active'));

				activeSlide?.classList.add('active');
				content[normalizedIndex]?.classList.add('active');

				if (isFullBg) {
					section.classList.add('fullscreen-mode');
					section.querySelector(`.fullscreen-bg-image.${blockId}`)?.classList.add('active');
				} else {
					section.classList.remove('fullscreen-mode');
					imgs[normalizedIndex]?.classList.add('active');
				}

				if (options.openPopup) section.classList.add('popup-active');
				else if (options.closePopup) section.classList.remove('popup-active');

				if (this.swiper) {
					setTimeout(() => {
						this.swiper.update();
						this.swiper.updateAutoHeight(300);
					}, 50);
				}
			},

			applyProgress(progress, opts) {
				if (!this.swiper) return;

				const normalizedProgress = this.clamp(progress, 0, 1);
				// Drive Swiper by translate so pinned scroll can scrub between slides smoothly.
				const translate = this.translateFromProgress(normalizedProgress);

				this.progress = normalizedProgress;
				this.isApplyingProgress = true;
				this.swiper.setTransition(0);
				this.swiper.setTranslate(translate);
				this.swiper.updateProgress(translate);
				this.swiper.updateActiveIndex();
				this.swiper.updateSlidesClasses();
				this.isApplyingProgress = false;
				window.setAccordionProgressBars(section, normalizedProgress, this.getCount());

				this.updateActive(this.getScrollIndex(normalizedProgress), opts);
			},

			setProgress(progress, opts) {
				const options = opts || {};
				this.applyProgress(progress, options);
				if (options.syncScroll) {
					this.syncScrollToProgress(this.progress, options.scrollDuration || 0);
				}
			},

			setIndex(index, opts) {
				const options = opts || {};
				const normalizedIndex = this.clamp(index, 0, this.getMaxIndex());
				const targetProgress = this.progressFromIndex(normalizedIndex);

				if (this.pinned) {
					this.setProgress(targetProgress, options);
					return;
				}

				this.progress = targetProgress;
				window.setAccordionProgressBars(section, targetProgress, this.getCount());
				this.updateActive(normalizedIndex, options);

				if (this.swiper) {
					this.swiper.slideTo(normalizedIndex, options.instant ? 0 : 350, false);
				}
			},

			progressFromScroll(scrollTop) {
				if (!this.trigger) return this.progress;
				const distance = this.trigger.end - this.trigger.start;
				if (!distance) return 0;
				return this.clamp((scrollTop - this.trigger.start) / distance, 0, 1);
			},

			syncToTrigger(opts) {
				const options = opts || {};
				if (this.pinned && this.trigger) {
					// Rebuild mobile slide/progress state from the current trigger after resize/refresh.
					this.setProgress(this.playProgressFromScrollProgress(this.progressFromScroll(
						window.pageYOffset || document.documentElement.scrollTop || 0
					)), { force: true });
					return;
				}
				this.setIndex(this.activeIndex < 0 ? 0 : this.activeIndex, Object.assign({}, options, {
					force: true,
					instant: true
				}));
			},

			syncScrollToProgress(progress, duration) {
				if (!this.trigger) return;

				const normalizedProgress = this.clamp(progress, 0, 1);
				const targetScrollProgress = this.scrollProgressFromPlayProgress(normalizedProgress);
				const targetScroll = this.trigger.start + ((this.trigger.end - this.trigger.start) * targetScrollProgress);
				const scrollState = { value: window.pageYOffset || document.documentElement.scrollTop || 0 };

				// Bridge from local interactions back to page scroll. When the user taps, drags,
				// or releases with momentum we compute the desired playhead, convert to ScrollTrigger
				// progress, and animate the document so GSAP and Swiper stay in sync.
				this.cancelScrollTween();

				if (!duration) {
					this.isSyncingScroll = true;
					window.scrollTo(0, targetScroll);
					window.requestAnimationFrame(() => {
						window.requestAnimationFrame(() => { this.isSyncingScroll = false; });
					});
					return;
				}

				this.isSyncingScroll = true;
				this.scrollTween = gsap.to(scrollState, {
					value: targetScroll,
					duration,
					ease: 'power2.out',
					overwrite: true,
					onUpdate: () => {
						window.scrollTo(0, scrollState.value);
						this.applyProgress(this.playProgressFromScrollProgress(this.progressFromScroll(scrollState.value)), { force: true });
					},
					onComplete: () => { this.isSyncingScroll = false; this.scrollTween = null; },
					onInterrupt: () => { this.isSyncingScroll = false; this.scrollTween = null; }
				});
			},

			handleScrollUpdate(self) {
				if (this.isSyncingScroll) return;
				this.setProgress(this.playProgressFromScrollProgress(self.progress));
			},

			getMomentumTargetProgress(velocity, deltaX) {
				const maxIndex = this.getMaxIndex();
				const signedVelocity = -velocity;
				const distanceRatio = Math.abs(deltaX) / Math.max(window.innerWidth || 1, 1);

				// Simulate a small amount of post-swipe momentum by projecting the gesture into
				// extra slides based on speed and travel distance, clamped to the slide range.
				if (!maxIndex) return this.progress;

				const extraSlides = this.clamp((Math.abs(signedVelocity) * 280) + (distanceRatio * 1.2), 0, 1.35);
				const currentSlidePosition = this.progress * maxIndex;
				const targetSlidePosition = this.clamp(
					currentSlidePosition + (Math.sign(signedVelocity || 0) * extraSlides),
					0,
					maxIndex
				);

				return targetSlidePosition / maxIndex;
			},

			handleTouchStart(event) {
				this.cancelScrollTween();
				this.touchStartX = event?.pageX || event?.touches?.[0]?.pageX || 0;
				this.touchStartTime = Date.now();
			},

			handleTouchMove() {
				if (!this.pinned || !this.swiper || this.isApplyingProgress) return;
				this.setProgress(this.progressFromTranslate(this.swiper.translate), { syncScroll: true });
			},

			handleTouchEnd(event) {
				if (!this.swiper) return;

				const endX = event?.pageX || event?.changedTouches?.[0]?.pageX || this.touchStartX;
				const deltaX = endX - this.touchStartX;
				const duration = Math.max(Date.now() - this.touchStartTime, 16);
				const velocity = deltaX / duration;

				if (!this.pinned) {
					this.setIndex(this.swiper.activeIndex, { force: true });
					return;
				}

				const baseProgress = this.progressFromTranslate(this.swiper.translate);
				const targetProgress = Math.abs(deltaX) > 6
					? this.getMomentumTargetProgress(velocity, deltaX)
					: this.progressFromIndex(this.getNearestIndex(baseProgress));

				this.setProgress(baseProgress);
				this.syncScrollToProgress(targetProgress, 0.35);
			}
		});

		window.__accordionScroll.mobileController = mobileController;
		window.setMobileAccordionIndex = (index, opts) => mobileController.setIndex(index, opts || {});
		window.setMobileAccordionProgress = (progress, opts) => mobileController.setProgress(progress, opts || {});
		window.updateActive = (index) => mobileController.updateActive(index, { force: true });

		// ─── Event delegation (vanilla, replaces jQuery namespaced click events) ─────
		// Handlers are stored so initSwiper can call removeDelegatedClicks() if this
		// block ever runs more than once in a dynamic-section context.
		const _clickHandlers = [];

		function addDelegatedClick(selector, handler) {
			const wrapped = (e) => {
				const target = e.target.closest(selector);
				if (!target || !section.contains(target)) return;
				handler.call(target, e);
			};
			document.addEventListener('click', wrapped);
			_clickHandlers.push(wrapped);
		}

		function removeDelegatedClicks() {
			_clickHandlers.forEach(h => document.removeEventListener('click', h));
			_clickHandlers.length = 0;
		}

		removeDelegatedClicks();

		addDelegatedClick('.left-accordion.mb_show .swiper-slide', function (e) {
			e.preventDefault();
			let index = parseInt(this.getAttribute('data-swiper-slide-index'), 10);
			if (isNaN(index)) index = Array.from(this.parentElement.children).indexOf(this);

			mobileController.setIndex(index, {
				instant: !mobileController.pinned,
				syncScroll: mobileController.pinned,
				scrollDuration: mobileController.pinned ? 0.3 : 0,
				openPopup: true,
				force: true
			});
		});

		addDelegatedClick('.main-acc-outer .swiper-button-prev, .main-acc-outer .swiper-button-next', function (e) {
			e.preventDefault();
			if (!mobileController.swiper) return;

			const currentIndex = mobileController.activeIndex >= 0
				? mobileController.activeIndex
				: mobileController.swiper.activeIndex;
			const delta = this.classList.contains('swiper-button-next') ? 1 : -1;
			const targetIndex = Math.max(0, Math.min(currentIndex + delta, mobileController.getMaxIndex()));

			if (targetIndex === currentIndex) return;

			mobileController.setIndex(targetIndex, {
				instant: !mobileController.pinned,
				syncScroll: mobileController.pinned,
				scrollDuration: mobileController.pinned ? 0.3 : 0,
				openPopup: section.classList.contains('popup-active'),
				force: true
			});
		});

		addDelegatedClick('.content-close', function (e) {
			e.preventDefault();
			mobileController.updateActive(mobileController.activeIndex < 0 ? 0 : mobileController.activeIndex, {
				force: true,
				closePopup: true
			});
		});

		// ─── Swiper init / teardown ───────────────────────────────────────────────
		function initSwiper(e) {
			if (e.matches) {
				section.classList.remove('popup-active', 'fullscreen-mode');

				if (!swiperInstance) {
					const swiperContainer = section.querySelector('.left-accordion.mb_show');
					if (!swiperContainer) return;

					swiperInstance = new Swiper(swiperContainer, {
						slidesPerView: 'auto',
						spaceBetween: 0,
						freeMode: false,
						grabCursor: true,
						loop: false,
						autoHeight: true,
						speed: 450,
						watchOverflow: false,
						watchSlidesProgress: true,
						centeredSlidesBounds: true,
						resistanceRatio: 0.65,
						followFinger: true,
						threshold: 4,
						navigation: {
							nextEl: section.querySelector('.main-acc-outer .swiper-button-next'),
							prevEl: section.querySelector('.main-acc-outer .swiper-button-prev')
						}
					});

					window.swiperInstance = swiperInstance;
					mobileController.swiper = swiperInstance;
					mobileController.activeIndex = -1;
					mobileController.progress = 0;
					mobileController.refresh();
					// Establish a known visual baseline before ScrollTrigger pins the mobile experience.
					mobileController.setIndex(0, { instant: true, force: true, closePopup: true });

					swiperInstance.on('touchStart', (_, event) => mobileController.handleTouchStart(event));
					swiperInstance.on('sliderMove', () => mobileController.handleTouchMove());
					swiperInstance.on('touchEnd', (_, event) => mobileController.handleTouchEnd(event));
					swiperInstance.on('slideChangeTransitionEnd', function () {
						if (mobileController.pinned || !hasInteracted) return;
						mobileController.setIndex(swiperInstance.activeIndex, { force: true });
					});

					setTimeout(function () {
						mobileController.setIndex(0, { instant: true, force: true });
						scheduleAccordionRefresh(true);
					}, 250);
				}

				window.initAccordionScrollListener(swiperInstance, section);
				scheduleAccordionRefresh(true);
				return;
			}

			section.classList.remove('popup-active', 'fullscreen-mode');

			if (swiperInstance) {
				swiperInstance.destroy(true, true);
				swiperInstance = null;
				window.swiperInstance = null;
				window.__accordionScroll.swiper = null;
				hasInteracted = false;
				mobileController.cancelScrollTween();
				mobileController.swiper = null;
				mobileController.activeIndex = -1;
				mobileController.progress = 0;
				mobileController.setTrigger(null);
				mobileController.setPinned(false);
			}

			window.initAccordionScrollListener(null, section);
			window.__accordionScroll.desktopController?.syncToTrigger?.({ force: true });
			scheduleAccordionRefresh(true);
		}

		initSwiper(mediaQuery);
		mediaQuery.addEventListener('change', initSwiper);
	});
})();
